/** @jsx jsx */
import React, { memo, useMemo } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { ListBlockSettings } from './ListBlockSettings';
import { Container } from 'reactstrap';
import { jsx } from '@emotion/react';
import { css } from '@emotion/react';
import { FormHTMLInline } from '@core/components/form';
import { useCourseBuilderContext } from '../../CourseBuilderPage';
import { useConfigureBlock } from '../../hooks';

export const LIST_BLOCK_AI_INSTRUCTION = `
LIST BLOCK - AI Instruction Format for Widget Variations

The "list" block supports two types of list widgets: numbered and bulleted. The returned JSON must strictly follow this format to be directly usable in the LMS frontend and backend.

Common Keys:
- type: "list"
- variation: One of the following:
    • "numberedList"
    • "bulletedList"
- data: object with structure:

{
  text: string,               // Full HTML <ol> or <ul> content with list items inside
  listColor: string,          // Hex color code used for list markers
}

Example (numbered list):
{
  "type": "list",
  "variation": "numberedList",
  "data": {
    "text": "<ol><li>First step</li><li>Second step</li></ol>",
    "listColor": "#FF6602",
  }
}

IMPORTANT - Headings and titles:
The list block has NO heading or title field. It renders ONLY the list items, nothing else.
Never put a section title, label, or introductory sentence inside the list itself.

If the source content has a title/heading above a list (e.g. "Proposed Solution", "Benefits", "Steps"),
you MUST emit a separate "text" block with that title immediately BEFORE the list block. Do not collapse a
titled section into a bare list — the title would be permanently lost.

- Output a "text" block (variation "paragraphWithHeading", "paragraphWithSubheading", or "subheading")
  whose "heading" holds the section title, then the "list" block for the items.
- If there is also an intro sentence before the bullets, place it in that text block's "paragraph",
  not inside the list.

Example (titled bulleted section -> heading text block + list block):
[
  {
    "type": "text",
    "variation": "paragraphWithSubheading",
    "data": {
      "heading": "Proposed Solution",
      "paragraph": "The workable option is to connect the relevant accounts directly. This gives:",
      "columnsWidths": "1"
    }
  },
  {
    "type": "list",
    "variation": "bulletedList",
    "data": {
      "text": "<ul><li>Complete visibility into all correspondence.</li><li>Real-time detection of new activity.</li></ul>",
      "listColor": "#FF6602"
    }
  }
]
`;

function defineListBlockForm() {
  return formGroup({
    text: formControl<string>({
      initialValue: `<ol><li>Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</li><li>Penatibus libero risus montes conubia pretium quisque ut fermentum, vulputate augue litora ornare fames turpis convallis imperdiet, curae etiam sed enim dictumst purus sit.</li><li>Tortor parturient gravida habitant dapibus et fusce felis nam nibh litora ridiculus aenean porttitor turpis pharetra.</li><li>Suscipit montes natoque aptent ex gravida orci ultrices volutpat nunc.</li></ol>`,
    }),

    listColor: formControl<string>({
      initialValue: '#FF6602',
    }),
    advance: defineBlockAdvanceForm({ paddingTop: 0, paddingBottom: 0 }),
  });
}

export type ListWidgetFormData = FormDataType<typeof defineListBlockForm>;
export type ListWidgetForm = ReturnType<typeof defineListBlockForm>;

export enum ListBlockVariation {
  numberedList = 'numberedList',
  bulletedList = 'bulletedList',
}

export const ListBlock = memo((props: CourseBuilder.BlockProps<ListBlockVariation>) => {
  const { variation, editable } = props;
  const form = useMemo(defineListBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const proposal = useCourseBuilderContext();
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);

  const listCss = useMemo(() => {
    if (primaryColor) {
      return css`
        li:before {
          background-color: ${primaryColor};
        }
      `;
    } else {
      return css`
        li:before {
          background-color: #45a8f7;
        }
      `;
    }
  }, [primaryColor]);

  useConfigureBlock({
    props,
    form,
    contentComponent: ListBlockSettings,
    contentProps: { form },
  });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-list', variation)} advanceForm={formValue.advance}>
        <div css={listCss}>
          <FormHTMLInline control={form.controls.text} readonly={!editable} glossarify />
        </div>
      </BlockContainer>
    </Container>
  );
});
